import API from "./api";
import { serviceHelper } from "./helper";

const CHUNK_SIZE = 140;

export const apiService = {
  getCurrentUser: getCurrentUserAsync,
  getArrayData: getArrayDataAsync,
  getArrayOData: getArrayODataAsync,
  getData: getDataAsync,
  getResourceStream: getDataByFullUrl,
  postData: postDataAsync,
  putData: putDataAsync,
  postImage: postImageAsync,
  deleteData: deleteDataAsync,
  patchData: patchDataAsync,
  getIfcStream: getDataByFullUrl,
  downloadDocumentVersion: downloadDocumentVersionAsync,
  uploadIfcStream: uploadIfcStreamAsync,
  getFolderData: getFolderDataAsync,
  getDataByEndpoint: getDataByEndpointAsync,
  patchIfcDocument: patchIfcDocumentAsync,
  checkUploadDocument: checkUploadDocumentAsync,
  getProjects: getProjectsAsync,
  deleteDataByEndpoint: deleteDataByEndpointAsync,
};

async function getCurrentUserAsync() {
  return await API.get(
    `${serviceHelper.getBaseURL()}user/current`,
    serviceHelper.authHeader()
  );
}

async function getProjectsAsync(ids) {
  try {
    const subIds = splitIds(ids);
    let promises = subIds.map((ids) => {
      return API.get(
        serviceHelper.getEndpointOData("project", ids),
        serviceHelper.authHeader()
      );
    });
    if (!ids.length) {
      promises = [API.get(
        serviceHelper.getEndpointOData("project", []),
        serviceHelper.authHeader()
      )];
    }

    const responses = await Promise.all(promises);
    let data = [];
    responses.forEach((res) => {
      data = [...data, ...res.data];
    });
    return data;
  } catch (err) {
    throw err;
  }
}

async function getArrayDataAsync(endpoint, ids) {
  try {
    const subIds = splitIds(ids);
    const promises = subIds.map((ids) => {
      return API.get(
        serviceHelper.getProjectEndpoint(endpoint, ids),
        serviceHelper.authHeader()
      );
    });

    const responses = await Promise.all(promises);
    let data = [];
    responses.forEach((res) => {
      data = [...data, ...res.data];
    });
    return data;
  } catch (err) {
    throw err;
  }
}

async function getArrayODataAsync(endpoint, ids) {
  try {
    let promises;
    if (ids && ids.length) {
      const subIds = splitIds(ids);
      promises = subIds.map((ids) => {
        return API.get(
          serviceHelper.getProjectEndpointOData(endpoint, ids),
          serviceHelper.authHeader()
        );
      });
    } else {
      promises = [API.get(
        serviceHelper.getProjectEndpointOData(endpoint, []),
        serviceHelper.authHeader()
      )];
    }

    const responses = await Promise.all(promises);
    let data = [];
    responses.forEach((res) => {
      data = [...data, ...res.data];
    });
    return data;
  } catch (err) {
    throw err;
  }
}

async function getDataAsync(endpoint) {
  return await API.get(
    serviceHelper.getProjectEndpointOData(endpoint, []),
    serviceHelper.authHeader()
  );
}

function splitIds(ids) {
  return ids.reduce((result, item, index) => {
    const chunkIndex = Math.floor(index / CHUNK_SIZE);
    if (!result[chunkIndex]) {
      result[chunkIndex] = [];
    }

    result[chunkIndex].push(item);
    return result;
  }, []);
}

async function getDataByFullUrl(url) {
  try {
    const response = await API.get(url);

    if (!response || response.status !== 200) {
      throw new Error(
        `Error: Received unexpected response status (${response.status})`
      );
    }
    return response.data;
  } catch (error) {
    console.error("Error while fetching the resource:", error.message || error);
    throw new Error(
      `Failed to fetch resource from URL ${url}: ${error.message}`
    );
  }
}

async function postDataAsync(endpoint, data) {
  return await API.post(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}${endpoint}`,
    data,
    serviceHelper.authHeader()
  );
}

async function putDataAsync(endpoint, data) {
  return await API.put(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}${endpoint}`,
    data,
    serviceHelper.authHeader()
  );
}

async function postImageAsync(endpoint, data) {
  return await API.post(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}resource/${endpoint}`,
    data,
    serviceHelper.uploadHeader()
  );
}

async function deleteDataAsync(endpoint, ids) {
  return await API.delete(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}${endpoint}`,
    serviceHelper.deleteConfig(ids)
  );
}

async function patchDataAsync(endpoint, data) {
  const token = serviceHelper.getToken();
  return await API.patch(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}${endpoint}`,
    JSON.stringify(data),
    {
      headers: {
        Authorization: `bearer ${token && token.bearer}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
}

async function patchIfcDocumentAsync(versionId) {
  return await API.patch(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}documentVersion/import/${versionId}`,
    undefined,
    serviceHelper.authHeader()
  );
}

async function downloadDocumentVersionAsync(data) {
  return await API.post(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}documentVersion/download`,
    data,
    serviceHelper.resourceHeader()
  );
}

async function uploadIfcStreamAsync(endpoint, data) {
  return await API.post(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}upload/${endpoint}`,
    data,
    serviceHelper.uploadHeader()
  );
}

async function getFolderDataAsync() {
  return await API.get(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}folder?includeDescendants=true&$filter=isTopLevel eq true`,
    serviceHelper.authHeader()
  );
}

async function getDataByEndpointAsync(endpoint) {
  return await API.get(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}${endpoint}`,
    serviceHelper.authHeader()
  );
}

async function checkUploadDocumentAsync(
  projId,
  folderId,
  docStatusId,
  fileName
) {
  const blob = new Blob(["test", { type: "text/plain" }]);
  const formData = new FormData();
  formData.append("file", blob, fileName);
  return await API.post(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}document/check?folderId=${folderId}&docStatusId=${docStatusId}`,
    formData,
    serviceHelper.uploadHeader()
  );
}

async function deleteDataByEndpointAsync(endpoint) {
  return await API.delete(
    `${serviceHelper.getBaseURL()}${serviceHelper.getProjectScope()}${endpoint}`,
    serviceHelper.authHeader()
  );
}
